import React, { useEffect, useMemo, useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import DirectorPanelState from './DirectorPanelState';
import AttendanceRiskSummaryStrip from './AttendanceRiskSummaryStrip';
import AttendanceRiskTable from './AttendanceRiskTable';
import AttendanceRiskDetailDrawer from './AttendanceRiskDetailDrawer';
import { buildPersonWorkspacePath } from '../../config/workforceRouting';
import { DIRECTOR_DATA_UPDATED_EVENT } from '../../utils/directorEvents';
import { buildQuery } from '../../utils/buildQuery';

const WINDOW_OPTIONS = [30, 60, 90];
const FILTER_OPTIONS = ['All', 'Critical', 'Concern', 'Watch'];
const GROUP_OPTIONS = [
  { value: 'none', label: 'People' },
  { value: 'supervisor', label: 'Group by Supervisor' },
  { value: 'location', label: 'Group by Location' }
];

function compareValues(left, right, direction = 'desc') {
  if (typeof left === 'number' && typeof right === 'number') {
    return direction === 'asc' ? left - right : right - left;
  }

  return direction === 'asc'
    ? String(left || '').localeCompare(String(right || ''))
    : String(right || '').localeCompare(String(left || ''));
}

function getAlertRank(level) {
  return {
    Critical: 3,
    Concern: 2,
    Watch: 1,
    Normal: 0
  }[level] || 0;
}

function getReviewRank(flag) {
  return {
    'escalation review': 3,
    'documented discussion review': 2,
    'coaching review': 1,
    '': 0,
    null: 0
  }[flag] || 0;
}

function buildRiskUrl({ groupBy, selectedLocationCode, windowDays }) {
  const locationCode = selectedLocationCode && selectedLocationCode !== 'ALL'
    ? selectedLocationCode
    : null;

  if (groupBy === 'none') {
    return `/api/events/attendance/employee-risk?${buildQuery({
      windowDays,
      locationCode
    })}`;
  }

  return `/api/events/attendance/employee-risk-rollups?${buildQuery({
    groupBy,
    windowDays,
    locationCode
  })}`;
}

function normalizePersonRow(row, windowDays) {
  const rowKey = row.employeeProfileId
    ? `employee:${row.employeeProfileId}`
    : row.managerId
      ? `manager:${row.managerId}`
      : `person:${row.displayName || 'unknown'}:${row.locationCode || 'na'}`;
  const selectedWindowSick = windowDays === 60 ? row.sick60 : windowDays === 90 ? row.sick90 : row.sick30;
  const selectedWindowAbsence = windowDays === 60 ? row.absence60 : windowDays === 90 ? row.absence90 : row.absence30;
  const selectedWindowLate = windowDays === 60 ? row.late60 : windowDays === 90 ? row.late90 : row.late30;

  return {
    ...row,
    rowKey,
    rowType: 'person',
    displayLabel: row.displayName || 'Unnamed employee',
    selectedWindowSick,
    selectedWindowAbsence,
    selectedWindowLate,
    totalIncidents: row.totalIncidents90 || 0
  };
}

function normalizeGroupRow(row, groupBy) {
  const keyPart = row.groupKey || 'unassigned';
  return {
    ...row,
    rowKey: `${groupBy}:${keyPart}`,
    rowType: 'group',
    displayLabel: row.groupLabel || 'Unassigned'
  };
}

function buildSummaryCards(groupBy, rows) {
  if (!rows.length) {
    return groupBy === 'none'
      ? [
          { label: 'Critical', value: 0 },
          { label: 'Concern', value: 0 },
          { label: 'Watch', value: 0 },
          { label: 'People', value: 0 },
          { label: 'Average At Work %', value: '0.0%' },
          { label: 'Lowest At Work %', value: '0.0%' }
        ]
      : [
          { label: 'Critical People', value: 0 },
          { label: 'Concern People', value: 0 },
          { label: 'Watch People', value: 0 },
          { label: 'Groups', value: 0 },
          { label: 'People', value: 0 },
          { label: 'Average At Work %', value: '0.0%' }
        ];
  }

  if (groupBy === 'none') {
    const averageAtWorkRate = rows.reduce((sum, row) => sum + Number(row.atWorkRate || 0), 0) / rows.length;
    const lowestAtWorkRate = Math.min(...rows.map((row) => Number(row.atWorkRate || 0)));
    return [
      { label: 'Critical', value: rows.filter((row) => row.alertLevel === 'Critical').length },
      { label: 'Concern', value: rows.filter((row) => row.alertLevel === 'Concern').length },
      { label: 'Watch', value: rows.filter((row) => row.alertLevel === 'Watch').length },
      { label: 'People', value: rows.length },
      { label: 'Average At Work %', value: `${averageAtWorkRate.toFixed(1)}%` },
      { label: 'Lowest At Work %', value: `${lowestAtWorkRate.toFixed(1)}%` }
    ];
  }

  const totalEmployees = rows.reduce((sum, row) => sum + Number(row.employeeCount || 0), 0);
  const totalCritical = rows.reduce((sum, row) => sum + Number(row.criticalCount || 0), 0);
  const totalConcern = rows.reduce((sum, row) => sum + Number(row.concernCount || 0), 0);
  const totalWatch = rows.reduce((sum, row) => sum + Number(row.watchCount || 0), 0);
  const averageAtWorkRate = rows.reduce((sum, row) => sum + Number(row.averageAtWorkRate || 0), 0) / rows.length;

  return [
    { label: 'Critical People', value: totalCritical },
    { label: 'Concern People', value: totalConcern },
    { label: 'Watch People', value: totalWatch },
    { label: 'Groups', value: rows.length },
    { label: 'People', value: totalEmployees },
    { label: 'Average At Work %', value: `${averageAtWorkRate.toFixed(1)}%` }
  ];
}

function DirectorAttendanceRiskPanel({
  selectedLocationCode,
  locationLabel,
  onManagerChange,
  onOpenPersonWorkspace,
  onOpenAttendanceEntry,
  onOpenManualPtoEntry
}) {
  const [groupBy, setGroupBy] = useState('none');
  const [windowDays, setWindowDays] = useState(30);
  const [filterLevel, setFilterLevel] = useState('All');
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState('alertLevel');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRowKey, setSelectedRowKey] = useState('');
  const [drilldown, setDrilldown] = useState(null);

  const riskUrl = useMemo(() => buildRiskUrl({
    groupBy,
    selectedLocationCode,
    windowDays
  }), [groupBy, selectedLocationCode, windowDays]);

  const { data: payload, isLoading, error, refetch } = useFetch(riskUrl);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetch]);

  useEffect(() => {
    if (groupBy === 'none') {
      setSortColumn('alertLevel');
      setSortDirection('desc');
    } else {
      setSortColumn('criticalCount');
      setSortDirection('desc');
    }
  }, [groupBy]);

  const rows = useMemo(() => {
    const sourceRows = Array.isArray(payload?.rows) ? payload.rows : [];
    return groupBy === 'none'
      ? sourceRows.map((row) => normalizePersonRow(row, windowDays))
      : sourceRows.map((row) => normalizeGroupRow(row, groupBy));
  }, [groupBy, payload, windowDays]);

  const handleGroupByChange = (value) => {
    setDrilldown(null);
    setGroupBy(value);
  };

  const filteredRows = useMemo(() => rows.filter((row) => {
    const matchesLevel = filterLevel === 'All'
      || (groupBy === 'none'
        ? row.alertLevel === filterLevel
        : (filterLevel === 'Critical' && Number(row.criticalCount || 0) > 0)
          || (filterLevel === 'Concern' && Number(row.concernCount || 0) > 0)
          || (filterLevel === 'Watch' && Number(row.watchCount || 0) > 0));

    const searchValue = search.trim().toLowerCase();
    const matchesSearch = !searchValue
      || String(row.displayLabel || '').toLowerCase().includes(searchValue)
      || String(row.locationCode || '').toLowerCase().includes(searchValue);

    const matchesDrilldown = !drilldown || (
      drilldown.type === 'supervisor'
        ? String(row.supervisorEmployeeId || '') === String(drilldown.supervisorEmployeeId || '')
        : String(row.locationId || '') === String(drilldown.locationId || '')
          || String(row.locationCode || '') === String(drilldown.locationCode || '')
    );

    return matchesLevel && matchesSearch && matchesDrilldown;
  }), [drilldown, filterLevel, groupBy, rows, search]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((left, right) => {
      if (sortColumn === 'alertLevel') {
        return compareValues(getAlertRank(left.alertLevel), getAlertRank(right.alertLevel), sortDirection);
      }
      if (sortColumn === 'disciplineReviewFlag') {
        return compareValues(getReviewRank(left.disciplineReviewFlag), getReviewRank(right.disciplineReviewFlag), sortDirection);
      }
      return compareValues(left[sortColumn], right[sortColumn], sortDirection);
    });
  }, [filteredRows, sortColumn, sortDirection]);

  const selectedRow = useMemo(
    () => sortedRows.find((row) => row.rowKey === selectedRowKey) || null,
    [selectedRowKey, sortedRows]
  );

  useEffect(() => {
    if (!sortedRows.length) {
      setSelectedRowKey('');
      return;
    }
    if (!sortedRows.some((row) => row.rowKey === selectedRowKey)) {
      setSelectedRowKey(sortedRows[0].rowKey);
    }
  }, [selectedRowKey, sortedRows]);

  const summaryCards = useMemo(
    () => buildSummaryCards(groupBy, filteredRows),
    [filteredRows, groupBy]
  );

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'displayLabel' || column === 'trend' || column === 'groupLabel' ? 'asc' : 'desc');
  };

  const handleView = (row) => {
    setSelectedRowKey(row.rowKey);
    if (groupBy === 'none') {
      if (typeof onOpenPersonWorkspace === 'function') {
        onOpenPersonWorkspace({
          ...row,
          workspacePath: buildPersonWorkspacePath({
            employeeProfileId: row.employeeProfileId,
            managerId: row.managerId
          })
        });
        return;
      }
      if (row.managerId) {
        onManagerChange?.(String(row.managerId));
      }
      return;
    }

    if (groupBy === 'supervisor') {
      setGroupBy('none');
      setDrilldown({
        type: 'supervisor',
        label: row.displayLabel,
        supervisorEmployeeId: row.supervisorEmployeeId || ''
      });
      return;
    }

    setGroupBy('none');
    setDrilldown({
      type: 'location',
      label: row.displayLabel,
      locationId: row.locationId || '',
      locationCode: row.locationCode || ''
    });
  };

  const handleLogEvent = (row) => {
    if (groupBy !== 'none') {
      return;
    }
    setSelectedRowKey(row.rowKey);
    onOpenAttendanceEntry?.(row);
  };

  const handleAddPto = (row) => {
    if (groupBy !== 'none') {
      return;
    }
    setSelectedRowKey(row.rowKey);
    onOpenManualPtoEntry?.(row);
  };

  const searchPlaceholder = groupBy === 'none'
    ? 'Person or location...'
    : groupBy === 'supervisor'
      ? 'Supervisor or location...'
      : 'Location...';

  return (
    <div className="role-section">
      <div className="role-section-header">
        <h2>Attendance Risk</h2>
        <span>{locationLabel}</span>
      </div>
      <p className="role-section-copy">
        People-first attendance risk with grouping as a reporting lens. Existing manager compatibility remains intact underneath this view.
      </p>

      <DirectorPanelState kind="loading" message={isLoading ? 'Loading attendance risk...' : ''} />
      <DirectorPanelState kind="error" message={error} />
      {!isLoading && !error && !rows.length && (
        <DirectorPanelState kind="empty" message="No attendance risk rows are available for the current scope yet." />
      )}

      <div className="director-toolbar">
        <div className="director-toolbar-group">
          <label>Group By</label>
          <div className="director-filter-chip-row">
            {GROUP_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`director-filter-chip ${groupBy === option.value ? 'active' : ''}`}
                onClick={() => handleGroupByChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="director-toolbar-group">
          <label>Window</label>
          <div className="director-filter-chip-row">
            {WINDOW_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`director-filter-chip ${windowDays === option ? 'active' : ''}`}
                onClick={() => setWindowDays(option)}
              >
                {option} Days
              </button>
            ))}
          </div>
        </div>

        <div className="director-toolbar-group">
          <label>Alert Filter</label>
          <div className="director-filter-chip-row">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`director-filter-chip ${filterLevel === option ? 'active' : ''}`}
                onClick={() => setFilterLevel(option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        <div className="director-toolbar-group director-toolbar-search">
          <label htmlFor="attendance-risk-search">Search</label>
          <input
            id="attendance-risk-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
          />
        </div>
      </div>

      {groupBy === 'none' && drilldown ? (
        <div className="director-toolbar">
          <div className="director-toolbar-group">
            <label>Focused People View</label>
            <div className="director-filter-chip-row">
              <span className="director-filter-chip active">
                {drilldown.type === 'supervisor' ? `Supervisor: ${drilldown.label}` : `Location: ${drilldown.label}`}
              </span>
              <button
                type="button"
                className="director-filter-chip"
                onClick={() => setDrilldown(null)}
              >
                Clear Focus
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AttendanceRiskSummaryStrip cards={summaryCards} />

      <div className="role-stack">
        <AttendanceRiskTable
          groupBy={groupBy}
          rows={sortedRows}
          selectedWindowDays={windowDays}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          onSelectRow={(row) => setSelectedRowKey(row.rowKey)}
          selectedRowKey={selectedRowKey}
          onView={handleView}
          onLogEvent={handleLogEvent}
          onAddPto={handleAddPto}
        />

        <AttendanceRiskDetailDrawer
          groupBy={groupBy}
          row={selectedRow}
          selectedWindowDays={windowDays}
          onViewPeople={selectedRow?.rowType === 'group' ? () => handleView(selectedRow) : null}
        />
      </div>
    </div>
  );
}

export default DirectorAttendanceRiskPanel;
